import React, { useMemo } from 'react'
import { ISelectOption } from 'components/forms/Selects/SelectOption'
import BarChartIcon from 'transfix-icons/build/BarChartIcon'
import CheckOutlineIcon from 'transfix-icons/build/CheckOutlineIcon'
import DropTrailerIcon from 'transfix-icons/build/DropTrailerIcon'
import ShipmentsIcon from 'transfix-icons/build/ShipmentsIcon'
import SteeringWheel from 'transfix-icons/build/SteeringWheelIcon'
import { IAutocompleteBaseOption } from 'transfix-ui/components/AutocompleteBase/types'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { Facet } from 'transfix-ui/components/Facet'
import { SingleSelect } from 'transfix-ui/components/SingleSelect'
import { Grid, Box, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'
import { DateRangeFilter } from 'apps/tms/components/Insights/Filters/DateRangeFilter'
import { IDateRange } from 'apps/tms/components/Insights/types'
import { EQUIPMENT_OPTIONS_INTERMODAL, MODE_OPTIONS_INTERMODAL } from 'apps/tms/constants/filters'
import { AutocompleteLocations } from 'apps/tms/pages/insights/components/AutocompleteLocations'
import {
  COVERAGE_TYPE_OPTIONS,
  DELIVERY_LOCATIONS_DEFAULT,
  PICKUP_LOCATIONS_DEFAULT,
} from 'apps/tms/pages/insights/constants'
import { OdPairLocation } from 'apps/tms/types/apiTypes'

export const locationsToAutocompleteBaseOption = ({
  city,
  stateCode,
}: OdPairLocation): IAutocompleteBaseOption => ({
  id: `${city}-${stateCode}`,
  text: `${city}, ${stateCode}`,
  groupIndex: -1,
})

const useOverviewFilterStyles = makeStyles(({ spacing }) => ({
  root: {
    marginBottom: spacing(4),
    background: brandColors.coolGray0,
  },
}))

interface IOverviewFilterProps extends IDateRange {
  assignmentMethod?: string
  assignmentMethodHandler: (dropdownOption?: ISelectOption) => void
  dateRangeHandler: (value: IDateRange) => void
  deliveryLocation: string
  deliveryLocationHandler: (_: any, option: IAutocompleteBaseOption) => void
  deliveryLocations: any
  equipment?: string
  equipmentHandler: (dropdownOption?: ISelectOption) => void
  includePrivateFleet: boolean
  includePrivateFleetHandler: () => void
  includeTopLanesBySpend: boolean
  includeTopLanesBySpendHandler: () => void
  isLoadingDeliveryLocations: boolean
  isLoadingPickupLocations: boolean
  mode?: string
  modeHandler: (dropdownOption?: ISelectOption) => void
  pickupLocation: string
  pickupLocationHandler: (_: any, option: IAutocompleteBaseOption) => void
  pickupLocations: any
  resetHandler: () => void
  showReset: boolean
}

export const OverviewFilter = (props: IOverviewFilterProps) => {
  const {
    assignmentMethod,
    assignmentMethodHandler,
    dateFilter,
    dateRangeHandler,
    deliveryLocation,
    deliveryLocationHandler,
    deliveryLocations,
    endAt,
    equipment,
    equipmentHandler,
    includePrivateFleet,
    includePrivateFleetHandler,
    includeTopLanesBySpend,
    includeTopLanesBySpendHandler,
    isLoadingDeliveryLocations,
    isLoadingPickupLocations,
    mode,
    modeHandler,
    pickupLocation,
    pickupLocationHandler,
    pickupLocations,
    resetHandler,
    showReset,
    startAt,
  } = props

  const classes = useOverviewFilterStyles()

  const autocompletePickupLocations = useMemo(
    () => [
      PICKUP_LOCATIONS_DEFAULT,
      ...(pickupLocations?.map(locationsToAutocompleteBaseOption) || []),
    ],
    [pickupLocations]
  )

  const autocompleteDeliveryLocations = useMemo(
    () => [
      DELIVERY_LOCATIONS_DEFAULT,
      ...(deliveryLocations?.map(locationsToAutocompleteBaseOption) || []),
    ],
    [deliveryLocations]
  )

  return (
    <Card className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <AutocompleteLocations
            disabled={includeTopLanesBySpend || isLoadingPickupLocations}
            isLoading={isLoadingPickupLocations}
            locations={autocompletePickupLocations}
            locationSelection={pickupLocation}
            onAutocompleteChange={pickupLocationHandler}
            type='pickup'
          />
        </Grid>
        <Grid item xs={6}>
          <AutocompleteLocations
            disabled={includeTopLanesBySpend || isLoadingDeliveryLocations}
            isLoading={isLoadingDeliveryLocations}
            locations={autocompleteDeliveryLocations}
            locationSelection={deliveryLocation}
            onAutocompleteChange={deliveryLocationHandler}
            type='delivery'
          />
        </Grid>
        <Grid item xs={12}>
          <Box display='flex'>
            <Box marginRight={1.5}>
              <Facet
                id='Transfix-Shipper-App-TopLanesBySpendSelect'
                dataTest='Transfix-Shipper-App-TopLanesBySpendSelect'
                icon={<BarChartIcon size='large' />}
                label='Top Lanes By Spend'
                selected={includeTopLanesBySpend}
                onClick={includeTopLanesBySpendHandler}
                isLoading={false}
              />
            </Box>
            <Box marginRight={1.5}>
              <SingleSelect
                id='Transfix-Shipper-App-ModeSelect'
                dataTest='Transfix-Shipper-App-ModeSelect'
                icon={<ShipmentsIcon size='large' />}
                label='All Modes'
                options={MODE_OPTIONS_INTERMODAL}
                onSelect={modeHandler}
                selected={mode}
              />
            </Box>
            <Box marginRight={1.5}>
              <SingleSelect
                id='Transfix-Shipper-App-EquipmentSelect'
                dataTest='Transfix-Shipper-App-EquipmentSelect'
                icon={<DropTrailerIcon size='large' />}
                label='All Equipment'
                options={EQUIPMENT_OPTIONS_INTERMODAL}
                onSelect={equipmentHandler}
                selected={equipment}
              />
            </Box>
            <Box marginRight={1.5}>
              <SingleSelect
                id='Transfix-Shipper-App-AssignmentMethodSelect'
                dataTest='Transfix-Shipper-App-AssignmentMethodSelect'
                icon={<CheckOutlineIcon size='large' />}
                label='Assignment Method'
                options={COVERAGE_TYPE_OPTIONS}
                onSelect={assignmentMethodHandler}
                selected={assignmentMethod}
              />
            </Box>
            <Box>
              <Facet
                id='Transfix-Shipper-App-PrivateFleetSelect'
                dataTest='Transfix-Shipper-App-PrivateFleetSelect'
                icon={<SteeringWheel size='large' />}
                label='Include Private Fleet'
                selected={includePrivateFleet}
                onClick={includePrivateFleetHandler}
              />
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box display='flex' justifyContent='space-between'>
            <DateRangeFilter value={{ startAt, endAt, dateFilter }} onChange={dateRangeHandler} />
            {showReset && (
              <Button
                onClick={resetHandler}
                color='secondary'
                data-test='Transfix-Shipper-App-ResetFilter'>
                Reset Filter
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Card>
  )
}
